import { useEffect, useState, useCallback } from "react";
import RichTextEditor from "../RichTextEditor";
import DragAndDropList from "../ArtistEdit/DragAndDropList";
import {
  Button,
  Modal,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Box,
  Checkbox,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAddEvent, useUpdateEvent, useDeleteEvent } from "../../hooks/useEventMutations";
import authApi from "../../api/authApi";
import uploadImage from "../../utils/uploadImage";
import toast from "react-hot-toast";
import { imageStyle, modalStyle, darkFieldSx, chipSx, addBtnSx } from "../ArtistEdit/style";
import defaultPhoto from "../../assets/default.jpeg";

const EventEdit = ({ open, eventId, closeModal }) => {
  const [state, setState] = useState({});
  const [event, setEvent] = useState({});
  const [genreField, setGenreField] = useState("");
  const [lineupField, setLineupField] = useState("");
  const [recordingField, setRecordingField] = useState("");

  const addEventMutation = useAddEvent();
  const updateEventMutation = useUpdateEvent(eventId);
  const deleteEventMutation = useDeleteEvent();

  useEffect(() => {
    if (!open) {
      setState({});
      setEvent({});
      setGenreField("");
      setLineupField("");
      setRecordingField("");
      return;
    }
    if (eventId) {
      (async function () {
        try {
          const response = await authApi.get(`/events/${eventId}`);
          const data = response.data?.data;
          if (data) {
            setState(data);
            setEvent(data);
          }
        } catch (error) {
          console.error("Failed to fetch event:", error);
          toast.error("Failed to load event");
        }
      })();
    }
  }, [eventId, open]);

  const handleChange = useCallback((key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDeleteLink = useCallback((linkToDelete) => {
    setState((prev) => ({
      ...prev,
      recordings: prev.recordings?.filter((l) => l !== linkToDelete),
    }));
  }, []);

  const handleDeleteGenre = useCallback((genreToDelete) => {
    setState((prev) => ({
      ...prev,
      genres: prev.genres?.filter((g) => g !== genreToDelete),
    }));
  }, []);

  const handleDeleteLineup = useCallback((artistToRemove) => {
    setState((prev) => ({
      ...prev,
      lineup: prev.lineup?.filter((a) => a !== artistToRemove),
    }));
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      await deleteEventMutation.mutateAsync(eventId);
      toast.success("Event deleted successfully");
      closeModal();
    } catch {
      toast.error("Failed to delete event");
    }
  }, [eventId, deleteEventMutation, closeModal]);

  const handleSubmit = useCallback(async () => {
    if (eventId) {
      const changes = {};
      Object.keys(state).forEach((key) => {
        if (JSON.stringify(state[key]) !== JSON.stringify(event[key])) {
          changes[key] = state[key];
        }
      });
      if (!Object.keys(changes).length) return;
      try {
        await updateEventMutation.mutateAsync(changes);
        toast.success("Event successfully updated");
        closeModal();
      } catch {
        toast.error("Failed to update event");
      }
    } else {
      try {
        await addEventMutation.mutateAsync(state);
        toast.success("Event successfully added");
        closeModal();
      } catch {
        toast.error("Failed to add event");
      }
    }
  }, [event, eventId, state, addEventMutation, updateEventMutation, closeModal]);

  const addGenre = useCallback(() => {
    if (genreField) {
      setState((prev) => ({
        ...prev,
        genres: Array.from(new Set([...(prev.genres || []), genreField])),
      }));
      setGenreField("");
    }
  }, [genreField]);

  const addLineup = useCallback(() => {
    if (lineupField) {
      setState((prev) => ({
        ...prev,
        lineup: Array.from(new Set([...(prev.lineup || []), lineupField])),
      }));
      setLineupField("");
    }
  }, [lineupField]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imageUrl = await uploadImage(file);
      if (imageUrl) setState((prev) => ({ ...prev, imageURL: imageUrl }));
    } catch {
      toast.error("Failed to upload image");
    }
  }, []);

  const isLoading =
    addEventMutation.isPending ||
    updateEventMutation.isPending ||
    deleteEventMutation.isPending;

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={modalStyle}>
        {/* Sticky header */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 4 },
            pt: { xs: 2, sm: 2.5 },
            pb: 2,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              letterSpacing: "0.03em",
            }}
          >
            {eventId ? "Edit Event" : "Create Event"}
          </Typography>

          <IconButton
            onClick={closeModal}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.4)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.8)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3 }, overflowY: "auto", flex: 1 }}>
          <Grid container spacing={2} direction="column" alignItems="center">

            {/* Image upload */}
            <Grid item>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
                id="event-upload-button"
              />
              <label htmlFor="event-upload-button">
                <img src={state.imageURL || defaultPhoto} alt="Event" style={imageStyle} />
              </label>
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "center", color: "rgba(255,255,255,0.3)", mt: 0.5 }}
              >
                Click image to change
              </Typography>
            </Grid>

            {/* Text fields */}
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: "8px",
                    border: state.showOnMainPage ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(255,255,255,0.08)",
                    backgroundColor: state.showOnMainPage ? "rgba(255,255,255,0.04)" : "transparent",
                    cursor: event.showOnMainPage ? "default" : "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => !event.showOnMainPage && handleChange("showOnMainPage", !state.showOnMainPage)}
                >
                  <Checkbox
                    checked={!!state.showOnMainPage}
                    disabled={!!event.showOnMainPage}
                    onChange={(e) => handleChange("showOnMainPage", e.target.checked)}
                    sx={{
                      p: 0,
                      color: "rgba(255,255,255,0.3)",
                      "&.Mui-checked": { color: "white" },
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.15)" },
                      "&.Mui-checked.Mui-disabled": { color: "rgba(255,255,255,0.45)" },
                    }}
                  />
                  <Box>
                    <Typography sx={{ color: state.showOnMainPage ? "white" : "rgba(255,255,255,0.6)", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.05em", userSelect: "none" }}>
                      Featured Event
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", userSelect: "none" }}>
                      Show on main page
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Event Name"
                  value={state.name || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  value={state.location || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("location", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={state.date || ""}
                  fullWidth
                  sx={darkFieldSx}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => handleChange("date", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={state.startTime || ""}
                  fullWidth
                  sx={darkFieldSx}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => handleChange("startTime", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Price"
                  value={state.price || ""}
                  fullWidth
                  sx={darkFieldSx}
                  onChange={(e) => handleChange("price", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <RichTextEditor
                  label="Description"
                  value={state.description || ""}
                  onChange={(value) => handleChange("description", value)}
                />
              </Grid>
            </Grid>

            {/* Genres */}
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <TextField
                  label="Add Genre"
                  value={genreField}
                  onChange={(e) => setGenreField(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addGenre()}
                  fullWidth
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={3}>
                <Button onClick={addGenre} variant="outlined" fullWidth sx={addBtnSx}>
                  Add
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1} pb={1} direction="row" flexWrap="wrap" useFlexGap>
                  {state.genres?.map((genre) => (
                    <Chip
                      sx={chipSx}
                      key={genre}
                      label={genre}
                      onDelete={() => handleDeleteGenre(genre)}
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>

            {/* Lineup */}
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <TextField
                  label="Add Lineup"
                  value={lineupField}
                  onChange={(e) => setLineupField(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addLineup()}
                  fullWidth
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={3}>
                <Button onClick={addLineup} variant="outlined" fullWidth sx={addBtnSx}>
                  Add
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1} pb={1} direction="row" flexWrap="wrap" useFlexGap>
                  {state.lineup?.map((artist) => (
                    <Chip
                      sx={chipSx}
                      key={artist}
                      label={artist}
                      onDelete={() => handleDeleteLineup(artist)}
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>

            {/* Recordings */}
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <TextField
                  label="Add Soundcloud Link"
                  value={recordingField}
                  onChange={(e) => setRecordingField(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && recordingField) {
                      setState((prev) => ({
                        ...prev,
                        recordings: Array.from(new Set([...(prev.recordings || []), recordingField])),
                      }));
                      setRecordingField("");
                    }
                  }}
                  fullWidth
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={addBtnSx}
                  onClick={() => {
                    if (recordingField) {
                      setState((prev) => ({
                        ...prev,
                        recordings: Array.from(new Set([...(prev.recordings || []), recordingField])),
                      }));
                      setRecordingField("");
                    }
                  }}
                >
                  Add
                </Button>
              </Grid>
              <Grid item xs={12}>
                <DragAndDropList
                  items={state.recordings || []}
                  handleDeleteLink={handleDeleteLink}
                  setItems={handleChange}
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Sticky footer */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 4 },
            py: 2,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
          }}
        >
          {eventId && (
            <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              disabled={isLoading}
              sx={{ borderColor: "rgba(255,80,80,0.5)", color: "rgba(255,100,100,0.9)" }}
            >
              {deleteEventMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            variant="outlined"
            disabled={isLoading}
            sx={{
              borderColor: "rgba(255,255,255,0.25)",
              color: "rgba(255,255,255,0.85)",
              "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.06)", color: "white" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.08)" },
            }}
          >
            {addEventMutation.isPending || updateEventMutation.isPending ? "Saving..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EventEdit;
